import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, Trash2, Users, X } from 'lucide-react'
import { useState } from 'react'
import { ApiError } from '@/api/client'
import {
  timeOffQuery,
  useAddTimeOff,
  useDeleteHours,
  useDeleteTimeOff,
  useSaveHours,
  workingHoursQuery,
  type WorkingHours,
} from '@/api/services'
import { NativeField } from '@/app/passes/controls'
import { personOption } from '@/app/services/person-option'
import { BranchSelect, ServicesShell, ToolbarSelect, type ServicesContext } from '@/app/services/services-shell'
import { hhmm, minutesOf, wallInstant, wallOf, WEEK_ORDER } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { Button, IconButton } from '@/ui/button'
import { CompactRow } from '@/ui/compact-row'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'

type Search = { member?: string; branch?: string }

const isId = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f-]{36}$/.test(value)

export const Route = createFileRoute('/_app/c/$orgId/services/hours')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(isId(search['member']) ? { member: search['member'] } : {}),
    ...(isId(search['branch']) ? { branch: search['branch'] } : {}),
  }),
  component: Hours,
})

function Hours() {
  const { orgId } = Route.useParams()
  return (
    <ServicesShell orgId={orgId} tab="hours">
      {(ctx) => <HoursScreen ctx={ctx} />}
    </ServicesShell>
  )
}

const DAY_NAMES = ['Неділя', 'Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота']

/// An interval's end as typed: '00:00' after a later start is midnight, the
/// server's '24:00' (a time input cannot say 24:00).
const endOf = (start: string, end: string): string => (end === '00:00' && start !== '00:00' ? '24:00' : end)

/// What a refusal of hours means here: an `invalid_range` is about the
/// interval, not about a freeze (the passes' meaning of the same code).
const hoursError = (error: unknown): string =>
  error instanceof ApiError && error.code === 'invalid_range' ? t.services.errors.invalidRange : errorText(error)

type Editing = Readonly<{ id: string | null; weekday: number; start: string; end: string }>

/// «Графік роботи»: one person's week in one club — intervals per weekday,
/// several a day («09:00–13:00 і 15:00–20:00»), on the club's wall clock —
/// and their time off. `manage_schedule` writes (within its clubs); `own`
/// reads its own and writes neither (specs/services.md §4).
function HoursScreen({ ctx }: { ctx: ServicesContext }) {
  const { orgId, can } = ctx
  const h = t.services.hours
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const people = [...ctx.members].sort((a, b) => ctx.nameOf(a.userId).localeCompare(ctx.nameOf(b.userId), 'uk'))
  const memberId = can.own ? ctx.access.userId : (search.member ?? people[0]?.userId ?? null)
  const branchId =
    search.branch && ctx.branches.some((b) => b.id === search.branch) ? search.branch : (ctx.branches[0]?.id ?? null)
  const zone = ctx.zoneOf(branchId)
  const hours = useQuery({ ...workingHoursQuery(orgId, memberId), enabled: memberId !== null })
  const save = useSaveHours(orgId)
  const remove = useDeleteHours(orgId)
  const [editing, setEditing] = useState<Editing | null>(null)

  const go = (next: Partial<Record<keyof Search, string | undefined>>) =>
    void navigate({
      search: (prev: Search) =>
        Object.fromEntries(Object.entries({ ...prev, ...next }).filter(([, value]) => value)) as Search,
      replace: true,
    })

  if (memberId === null) return <EmptyState icon={<Users />} title={h.noMembers} />

  const mine = (hours.data ?? []).filter((row) => row.branchId === branchId)
  const ofDay = (weekday: number) =>
    mine.filter((row) => row.weekday === weekday).sort((a, b) => a.startLocal.localeCompare(b.startLocal))

  const startEdit = (next: Editing) => {
    save.reset()
    setEditing(next)
  }
  const wrong = editing !== null && minutesOf(endOf(editing.start, editing.end)) <= minutesOf(editing.start)
  const commit = () => {
    if (!editing || wrong || !memberId) return
    save.mutate(
      {
        id: editing.id,
        input: {
          userId: memberId,
          branchId,
          weekday: editing.weekday,
          startLocal: editing.start,
          endLocal: endOf(editing.start, editing.end),
        },
      },
      { onSuccess: () => setEditing(null) },
    )
  }

  const editor = (weekday: number) =>
    editing && editing.weekday === weekday ? (
      <div className="flex flex-col gap-2 px-4 pb-3.5">
        <div className="flex flex-wrap items-end gap-3">
          <NativeField className="w-[132px]" label={h.from} type="time" value={editing.start} onChange={(start) => setEditing({ ...editing, start })} />
          <NativeField
            className="w-[132px]"
            label={h.to}
            type="time"
            value={editing.end}
            onChange={(end) => setEditing({ ...editing, end })}
            error={wrong ? h.invalid : undefined}
          />
          <div className="flex gap-2 pb-1">
            <Button variant="quiet" compact onClick={() => setEditing(null)}>
              {t.app.cancel}
            </Button>
            <Button compact busy={save.isPending} disabled={wrong} onClick={commit}>
              {h.save}
            </Button>
          </div>
        </div>
        {save.isError ? <InlineBanner kind="error" message={hoursError(save.error)} /> : null}
      </div>
    ) : null

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-2">
        {can.own ? null : (
          <ToolbarSelect
            label={h.member}
            value={memberId}
            onChange={(member) => {
              setEditing(null)
              go({ member })
            }}
            options={people.map((m) => personOption(ctx, m.userId))}
          />
        )}
        <BranchSelect
          branches={ctx.branches}
          value={branchId}
          onChange={(branch) => {
            setEditing(null)
            go({ branch: branch ?? undefined })
          }}
        />
      </div>

      {can.manageHours ? null : <InlineBanner kind="info" message={h.readOnly} />}

      {hours.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : hours.isError ? (
        <InlineBanner kind="error" message={errorText(hours.error)} action={{ label: t.app.retry, onClick: () => void hours.refetch() }} />
      ) : (
        <SettingsSection title={h.week} meta={ctx.branches.find((b) => b.id === branchId)?.name} footer={h.weekHint}>
          {WEEK_ORDER.map((weekday) => {
            const rows = ofDay(weekday)
            return (
              <div key={weekday} className="compact-row relative flex flex-col">
                <div className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 py-2.5 pr-2 pl-4">
                  <span className="w-28 shrink-0 text-callout font-medium text-text">{DAY_NAMES[weekday]}</span>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    {rows.length === 0 ? <span className="text-callout text-muted">{h.dayOff}</span> : null}
                    {rows.map((row: WorkingHours) => {
                      const range = `${row.startLocal}–${row.endLocal}`
                      return (
                        <span key={row.id} className="inline-flex h-9 items-center rounded-full bg-surface-2 text-caption font-semibold text-text tabular">
                          {can.manageHours ? (
                            <>
                              <button
                                type="button"
                                aria-label={h.edit(range)}
                                onClick={() =>
                                  startEdit({ id: row.id, weekday, start: row.startLocal, end: row.endLocal === '24:00' ? '00:00' : row.endLocal })
                                }
                                className="h-9 rounded-l-full pr-1.5 pl-3.5 hover:text-text-2"
                              >
                                {range}
                              </button>
                              <button
                                type="button"
                                aria-label={h.remove(range)}
                                onClick={() => remove.mutate(row.id)}
                                className="mr-1 inline-flex size-7 items-center justify-center rounded-full text-text-2 hover:bg-[color-mix(in_srgb,var(--text)_8%,transparent)] hover:text-text"
                              >
                                <X className="size-3.5" />
                              </button>
                            </>
                          ) : (
                            <span className="px-3.5">{range}</span>
                          )}
                        </span>
                      )
                    })}
                  </div>
                  {can.manageHours ? (
                    <IconButton
                      label={`${h.add}: ${DAY_NAMES[weekday]}`}
                      onClick={() => {
                        const last = rows[rows.length - 1]
                        const start = last ? (last.endLocal === '24:00' ? '20:00' : last.endLocal) : '09:00'
                        startEdit({ id: null, weekday, start, end: hhmm(Math.min(1440, minutesOf(start) + 240) % 1440) })
                      }}
                    >
                      <Plus className="size-[18px]" />
                    </IconButton>
                  ) : null}
                </div>
                {editor(weekday)}
              </div>
            )
          })}
          {remove.isError ? (
            <div className="p-3">
              <InlineBanner kind="error" message={errorText(remove.error)} />
            </div>
          ) : null}
        </SettingsSection>
      )}

      <TimeOffSection ctx={ctx} memberId={memberId} zone={zone} />
    </div>
  )
}

/// «Відсутності»: what is ahead, and a form to add one. A whole-company
/// absence — time off has no club (§4).
function TimeOffSection({ ctx, memberId, zone }: { ctx: ServicesContext; memberId: string; zone: string }) {
  const h = t.services.hours
  const [since] = useState(() => new Date().toISOString())
  const list = useQuery(timeOffQuery(ctx.orgId, memberId, since))
  const add = useAddTimeOff(ctx.orgId)
  const remove = useDeleteTimeOff(ctx.orgId)
  const today = wallOf(new Date(), zone).date
  const [draft, setDraft] = useState<{ fromDate: string; fromTime: string; toDate: string; toTime: string; note: string } | null>(null)

  const span = (iso: string) =>
    new Intl.DateTimeFormat('uk-UA', { timeZone: zone, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))

  const fromAt = draft ? wallInstant(draft.fromDate, minutesOf(draft.fromTime || '00:00'), zone) : null
  const toAt = draft ? wallInstant(draft.toDate, minutesOf(draft.toTime || '00:00'), zone) : null
  const wrong = fromAt !== null && toAt !== null && toAt.getTime() <= fromAt.getTime()

  return (
    <SettingsSection title={h.timeOff} meta={list.data?.length ? String(list.data.length) : undefined} footer={`${h.timeOffHint} ${h.timeOffBooked}`}>
      {list.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : list.isError ? (
        <div className="p-3">
          <InlineBanner kind="error" message={errorText(list.error)} action={{ label: t.app.retry, onClick: () => void list.refetch() }} />
        </div>
      ) : (
        <>
          {(list.data ?? []).length === 0 && !draft ? <p className="px-4 py-3.5 text-callout text-muted">{h.timeOffNone}</p> : null}
          {(list.data ?? []).map((off) => (
            <CompactRow
              key={off.id}
              title={`${span(off.startsAt)} – ${span(off.endsAt)}`}
              subtitle={off.note ?? undefined}
              actions={
                ctx.can.manageHours ? (
                  <IconButton label={h.timeOffRemove} onClick={() => remove.mutate(off.id)}>
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : undefined
              }
            />
          ))}
          {ctx.can.manageHours ? (
            draft ? (
              <div className="compact-row relative flex flex-col gap-3 px-4 py-4">
                <div className="flex flex-wrap items-end gap-3">
                  <NativeField className="w-[170px]" label={h.timeOffFrom} type="date" value={draft.fromDate} onChange={(fromDate) => setDraft({ ...draft, fromDate })} />
                  <NativeField className="w-[120px]" label={h.from} type="time" value={draft.fromTime} onChange={(fromTime) => setDraft({ ...draft, fromTime })} />
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <NativeField className="w-[170px]" label={h.timeOffTo} type="date" value={draft.toDate} onChange={(toDate) => setDraft({ ...draft, toDate })} />
                  <NativeField
                    className="w-[120px]"
                    label={h.to}
                    type="time"
                    value={draft.toTime}
                    onChange={(toTime) => setDraft({ ...draft, toTime })}
                    error={wrong ? h.invalid : undefined}
                  />
                </div>
                <Field
                  label={h.timeOffNote}
                  placeholder={h.timeOffNotePlaceholder}
                  value={draft.note}
                  onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                />
                {add.isError ? <InlineBanner kind="error" message={hoursError(add.error)} /> : null}
                <div className="flex justify-end gap-2">
                  <Button variant="quiet" compact onClick={() => setDraft(null)}>
                    {t.app.cancel}
                  </Button>
                  <Button
                    compact
                    busy={add.isPending}
                    disabled={wrong}
                    onClick={() => {
                      if (!fromAt || !toAt || wrong) return
                      add.mutate(
                        { userId: memberId, startsAt: fromAt.toISOString(), endsAt: toAt.toISOString(), note: draft.note.trim() || null },
                        { onSuccess: () => setDraft(null) },
                      )
                    }}
                  >
                    {h.save}
                  </Button>
                </div>
              </div>
            ) : (
              <CompactRow
                title={h.timeOffAdd}
                accessory="add"
                onClick={() => {
                  add.reset()
                  setDraft({ fromDate: today, fromTime: '09:00', toDate: today, toTime: '18:00', note: '' })
                }}
              />
            )
          ) : null}
          {remove.isError ? (
            <div className="p-3">
              <InlineBanner kind="error" message={errorText(remove.error)} />
            </div>
          ) : null}
        </>
      )}
    </SettingsSection>
  )
}
